import React, {forwardRef, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, useSlotRecipe} from '@chakra-ui/react3'
import {useIntl} from 'react-intl'
import NavigationParent from '../../navigation-parent'
import NavigationSeparator from '../../navigation-separator'
import NavigationLink from '../../navigation-link'
import NavigationHide from '../../hide'
import NavigationTeaser from '../../navigation-teaser'
import useNotImplemented from '../../../../../hooks/use-not-implemented'
import {flyoutNavigationRecipe} from './flyout-navigation.recipe'
import {itemIndex, withStaggerIndices} from './flyout-stagger'
import {trackNavigationClick} from '../../../../../tracking/analytics'

const NAV_LVL_1_MAP = {
    navigationParentLevel1: NavigationParent,
    navigationSeparator: NavigationSeparator,
    navigationLink: NavigationLink
}

/**
 * Diagnostic for a level-1 category that cannot open — its `level2ModuleId`
 * matched no published level-2 module. An editor message, intentionally
 * untranslated: a shopper should never see it.
 *
 * @param {Object} item - The level-1 parent entry.
 * @returns {string}
 */
const level1NoModuleMessage = (item) =>
    `Xleap nav: level-1 "${item.title}" cannot open — level2ModuleId "${
        item.level2ModuleId ?? 'none'
    }" matched no published level-2 module.`

/**
 * Resolves a level-1 entry's interaction props from its resolved shape.
 *
 * A parent that resolved a level-2 module opens its pane on click and names it,
 * every selectable entry pointing at the same pane because they swap its
 * content rather than each bringing one of their own. It reports open only once
 * picked, not while desktop merely falls back to showing the first entry's
 * pane: one value serves both viewports, and this way activating an entry
 * always turns the state from shut to open — the only signal there is, since
 * focus stays put (STOK-17460). A parent whose module failed to resolve gets a
 * diagnostic toast instead of silently doing nothing. Every other entry type
 * carries no interaction of its own.
 *
 * @param {Object} item - The resolved level-1 entry.
 * @param {Object} handlers
 * @param {string} [handlers.selectedId] - `level2ModuleId` of the entry picked.
 * @param {string} [handlers.paneId] - Id of the pane the entries open.
 * @param {Function} handlers.onSelect - Called with the entry's `level2ModuleId`.
 * @param {Function} handlers.notImplemented - Shows the diagnostic toast.
 * @returns {Object} Props merged onto the resolved component.
 */
const resolveLevel1ItemProps = (item, {selectedId, paneId, onSelect, notImplemented}) => {
    const {level2ModuleId, level2Module} = item
    if (level2ModuleId && level2Module) {
        return {
            isActive: level2ModuleId === selectedId,
            controls: paneId,
            onClick: () => {
                trackNavigationClick(1, item.title)
                onSelect(level2ModuleId)
            }
        }
    }
    if (item.type === 'navigationParentLevel1') {
        return {
            onClick: (e) => {
                trackNavigationClick(1, item.title)
                notImplemented(e, level1NoModuleMessage(item))
            }
        }
    }
    return {onClick: () => trackNavigationClick(1, item.title)}
}

/**
 * Level-1 entries carry the accent flag flat on the item, while the shared
 * navigation components read it from `variant`. Promote the flag into the
 * shape the components already understand so the level-1 rows render the
 * authored accent colour.
 *
 * @param {Object} item - The resolved level-1 entry.
 * @returns {Object|undefined} The variant object the shared nav components read.
 */
const resolveLevel1Variant = (item) =>
    item.accentColor === undefined ? undefined : {type: 'link', accentColor: item.accentColor}

/** Entries whose type has no component render nothing at all. */
const isRenderableLevel1 = (item) => Boolean(NAV_LVL_1_MAP[item.type])

/**
 * Level-1 pane: the category list and, on mobile, the teaser column beside it.
 *
 * On mobile the list and the teaser column are one pane and travel as one, so
 * they are one box. On desktop that box is dissolved — see `level1Container` in
 * the recipe — and the two slots below become the flex items of the pane row.
 * The tool-nav row belongs to the same mobile view but stays outside, as the
 * tree root's last child.
 *
 * @component
 * @param {Object} props
 * @param {Array} props.elements - Resolved level-1 entries.
 * @param {Array} [props.teasers] - Level-1 teasers, already filtered for content.
 * @param {string} [props.selectedId] - `level2ModuleId` of the entry picked.
 * @param {string} [props.paneId] - Id of the pane the entries open.
 * @param {Function} props.onSelect - Called with the picked entry's `level2ModuleId`.
 * @returns {JSX.Element}
 */
const FlyoutNavigationLevel1 = forwardRef(
    ({elements = [], teasers = [], selectedId, paneId, onSelect}, ref) => {
        const recipe = useSlotRecipe({recipe: flyoutNavigationRecipe})
        const styles = useMemo(() => recipe(), [recipe])
        const {formatMessage} = useIntl()
        const notImplemented = useNotImplemented()

        // Renderable rows paired with their place in the staggered reveal.
        const staggeredElements = useMemo(
            () => withStaggerIndices(elements, isRenderableLevel1),
            [elements]
        )

        return (
            <Box ref={ref} css={styles.level1Container}>
                <Box
                    as="ul"
                    css={styles.itemsContainer}
                    // Named so it is distinguishable from the level-2 list
                    // that sits beside it on desktop.
                    aria-label={formatMessage({
                        id: 'leap.navigation.level1.items',
                        defaultMessage: 'Categories'
                    })}
                >
                    {staggeredElements.map(({item: resolvedItem, mobile, desktop}, idx) => {
                        const Component = NAV_LVL_1_MAP[resolvedItem.type]
                        const hide =
                            resolvedItem.type === 'navigationParentLevel1'
                                ? 'none'
                                : resolvedItem.hide

                        return (
                            <NavigationHide asChild hide={hide} key={idx}>
                                <Box as="li" css={styles.item} style={itemIndex({mobile, desktop})}>
                                    <Component
                                        {...resolvedItem}
                                        variant={
                                            resolvedItem.variant ??
                                            resolveLevel1Variant(resolvedItem)
                                        }
                                        {...resolveLevel1ItemProps(resolvedItem, {
                                            selectedId,
                                            paneId,
                                            onSelect,
                                            notImplemented
                                        })}
                                    />
                                </Box>
                            </NavigationHide>
                        )
                    })}
                </Box>
                <Box css={styles.teasersContainer}>
                    {/* `teasers` holds teasers and nothing else, so the
                        entry's own `type` is not consulted — it is a hidden
                        CMS default that content authored before it existed
                        does not carry. Levels 2 and 3 render their teaser
                        columns the same way. */}
                    {teasers.map((item, idx) => (
                        <NavigationHide hide={item.hide} key={idx}>
                            <NavigationTeaser {...item} variant="small" />
                        </NavigationHide>
                    ))}
                </Box>
            </Box>
        )
    }
)

FlyoutNavigationLevel1.propTypes = {
    elements: PropTypes.array,
    teasers: PropTypes.array,
    selectedId: PropTypes.string,
    paneId: PropTypes.string,
    onSelect: PropTypes.func.isRequired
}

if (process.env.NODE_ENV !== 'production') {
    FlyoutNavigationLevel1.displayName = 'FlyoutNavigationLevel1'
}

export default FlyoutNavigationLevel1
